import { Calendar, MemberMap } from '@nextforce/api'
import { t } from 'app/i18l'
import { cloneDeep, isEqual } from 'lodash'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import uuid from 'react-native-uuid'
import { SettingValue } from 'settings/types'
import { useStoredState } from 'shared/hooks/stored_state'
import { useRecentStoresContext } from 'stores/context'
import {
  BooleanFilter,
  Filters,
  MultiSelectFilter,
  Preset,
  SingleSelectFilter,
} from './types'

export type FilterContextValue = {
  filters: Filters
  setFilters: React.Dispatch<React.SetStateAction<Filters>>
  crewMembers: MemberMap
  currentFilters: Filters
  setCurrentFilters: React.Dispatch<React.SetStateAction<Filters>>
  setCurrentFiltersWithToggle: (newFilters: Filters) => void
  setFiltersWithToggle: (newFilters: Filters) => void
  setCrewMemberFilter: (members: MemberMap) => void
  activeFilterNames: (inputFilters?: Filters) => string
  savedPresets: Preset[] | undefined
  setSavedPresets: React.Dispatch<React.SetStateAction<Preset[] | undefined>>
  selectedPresetIndex: number | undefined
  setSelectedPresetIndex: React.Dispatch<
    React.SetStateAction<number | undefined>
  >
  currentSelectedPresetIndex: number | undefined
  setCurrentSelectedPresetIndex: React.Dispatch<
    React.SetStateAction<number | undefined>
  >
  scheduleCalendar: Calendar | undefined
  setScheduleCalendar: React.Dispatch<
    React.SetStateAction<Calendar | undefined>
  >
}

export const FilterContext = createContext({} as FilterContextValue)

interface Props {
  children: React.ReactNode
}

export function FilterProvider(props: Props) {
  const { activeStoreId } = useRecentStoresContext()
  const [scheduleCalendar, setScheduleCalendar] = useState<
    Calendar | undefined
  >()
  const [crewMembers, setCrewMembers] = useState<MemberMap>({})

  const [savedPresets, setSavedPresets] =
    useStoredState<Preset[]>('saved_presets')

  const toggleFilters = useCallback(
    (newFilters: Filters) => {
      const updatedFilters = cloneDeep(newFilters)
      if (scheduleCalendar) {
        if (scheduleCalendar === 'store') {
          const {
            crewMembers: crewMember,
            positions,
            shiftsWithAlerts,
            nextShifts,
            shiftsWithMinor,
            criticalShifts,
            shiftsWithBonus,
            shiftsWithInterest,
          } = updatedFilters

          if (nextShifts.on) {
            positions.visible = true
            shiftsWithAlerts.visible = true
            nextShifts.visible = true

            crewMember.visible = false

            shiftsWithMinor.visible = false
            shiftsWithMinor.on = false

            criticalShifts.visible = true
            shiftsWithBonus.visible = true
            shiftsWithInterest.visible = true
          } else {
            positions.visible = true
            shiftsWithAlerts.visible = true
            nextShifts.visible = true

            crewMember.visible = true
            shiftsWithMinor.visible = true

            criticalShifts.visible = false
            criticalShifts.on = false

            shiftsWithBonus.visible = false
            shiftsWithBonus.on = false

            shiftsWithInterest.visible = false
            shiftsWithInterest.on = false
          }

          if (shiftsWithAlerts.on && !nextShifts.on) {
            crewMember.visible = true
            shiftsWithMinor.visible = true
          }

          if (!crewMember.visible) {
            crewMember.selectedIds = []
            setCrewMembers({})
          }
        } else {
          const { incentives, positions, startTime, stores } = updatedFilters
          incentives.visible = true
          positions.visible = true
          startTime.visible = true
          stores.visible = true
        }
      }
      return updatedFilters
    },
    [scheduleCalendar]
  )

  const [filters, setFilters] = useState<Filters>(
    toggleFilters(getDefaultFilters())
  )

  const [currentFilters, setCurrentFilters] = useState<Filters>(
    cloneDeep(filters)
  )

  const [selectedPresetIndex, setSelectedPresetIndex] = useState<
    number | undefined
  >()

  const [currentSelectedPresetIndex, setCurrentSelectedPresetIndex] = useState<
    number | undefined
  >(selectedPresetIndex)

  const setCurrentFiltersWithToggle = useCallback(
    (newFilters: Filters) => {
      setCurrentFilters(toggleFilters(newFilters))
    },
    [toggleFilters]
  )

  const setFiltersWithToggle = useCallback(
    (newFilters: Filters) => {
      setFilters(toggleFilters(newFilters))
    },
    [toggleFilters]
  )

  const setCrewMemberFilter = useCallback(
    (members: MemberMap) => {
      const crewMemberFilter = {
        ...currentFilters['crewMembers'],
        selectedIds: Object.keys(members),
      }

      const updatedFilters = cloneDeep(currentFilters)
      updatedFilters.crewMembers = crewMemberFilter

      setCrewMembers(members)

      setCurrentFiltersWithToggle(updatedFilters)
    },
    [currentFilters, setCurrentFiltersWithToggle]
  )

  const clearAllFilters = useCallback(() => {
    setFilters(toggleFilters(getDefaultFilters()))
    setCrewMembers({})
  }, [toggleFilters])

  useEffect(() => {
    clearAllFilters()
  }, [activeStoreId, clearAllFilters])

  const activeFilterNames = useCallback(
    (inputFilters: Filters = filters) => {
      let names = ''
      for (const [name, filter] of Object.entries(inputFilters)) {
        switch (filter.type) {
          case 'boolean': {
            if (filter.on) {
              names += `; ${t(filter.shortName)}`
            }
            break
          }
          case 'singleSelect': {
            if (filter.selectedId !== '0') {
              names += `; ${
                filter.values.find(
                  filterSetting => filterSetting.id === filter.selectedId
                )?.name
              }`
            }
            break
          }
          case 'multiSelect': {
            if (name === 'crewMembers') {
              if (filter.selectedIds.length !== 0) {
                names += `; ${filter.selectedIds.length} ${t('member')}${
                  filter.selectedIds.length > 1 ? 's' : ''
                }`
              }

              break
            } else {
              if (
                filter.values.length !== 0 &&
                filter.selectedIds.length !== 0
              ) {
                const selectedPositions = filter.values
                  .filter(filterValue =>
                    filter.selectedIds.includes(filterValue.id)
                  )
                  .slice(0, 2)

                names += ';'

                selectedPositions.forEach(
                  position => (names += ` ${position.name},`)
                )

                names = names.slice(0, -1)
                const remainingPositions = filter.selectedIds.length - 2
                if (remainingPositions > 0) {
                  names += ` + ${remainingPositions}`
                }
              }
              break
            }
          }
        }
      }

      if (names === '') {
        return t('allShifts')
      }
      return (
        (selectedPresetIndex !== undefined
          ? `${selectedPresetIndex + 1}: `
          : '') + names.slice(2)
      )
    },
    [filters, selectedPresetIndex]
  )

  const value: FilterContextValue = {
    filters,
    setFilters,
    crewMembers,
    currentFilters,
    setCurrentFilters,
    setCurrentFiltersWithToggle,
    setFiltersWithToggle,
    setCrewMemberFilter,
    activeFilterNames,
    savedPresets,
    setSavedPresets,
    selectedPresetIndex,
    setSelectedPresetIndex,
    currentSelectedPresetIndex,
    setCurrentSelectedPresetIndex,
    scheduleCalendar,
    setScheduleCalendar,
  }

  return (
    <FilterContext.Provider value={value}>
      {props.children}
    </FilterContext.Provider>
  )
}

export function useFiltersContext() {
  return useContext<FilterContextValue>(FilterContext)
}

export enum defaultStartTimeListEnum {
  allDay,
  breakfast,
  lunch,
  dinner,
}

export enum defaultIncentivesEnum {
  allNextShifts,
  nextShiftsWithIncentives,
  nextShiftsWithIncentivesMoreThan10Dollars,
  nextShiftsWithIncentivesMoreThan20Dollars,
  nextShiftsWithoutIncentives,
}

const enumToSettingValues = (data: any): SettingValue[] => {
  return Object.keys(data)
    .filter(key => isNaN(Number(key)))
    .map(key => {
      return {
        id: data[key].toString(),
        name: t(key),
        locked: false,
      }
    })
}

export function getDefaultFilters(): Filters {
  const defaultBooleanFilter = {
    type: 'boolean',
    on: false,
    visible: false,
  } as const
  const defaultMultiSelectFilter = {
    type: 'multiSelect',
    selectedIds: [],
    visible: false,
  } as const
  const defaultSingleSelectFilter = {
    type: 'singleSelect',
    selectedId: '0',
    visible: false,
  } as const

  return {
    nextShifts: {
      name: 'nextShifts',
      shortName: 'next',
      id: uuid.v4() as string,
      ...defaultBooleanFilter,
    },
    shiftsWithAlerts: {
      name: 'shiftsWithAlerts',
      shortName: 'alerts',
      id: uuid.v4() as string,
      ...defaultBooleanFilter,
    },
    criticalShifts: {
      name: 'criticalShifts',
      shortName: 'critical',
      id: uuid.v4() as string,
      ...defaultBooleanFilter,
    },
    shiftsWithMinor: {
      name: 'shiftsWithMinor',
      shortName: 'minor',
      id: uuid.v4() as string,
      ...defaultBooleanFilter,
    },
    shiftsWithInterest: {
      name: 'shiftsWithInterest',
      shortName: 'interest',
      id: uuid.v4() as string,
      ...defaultBooleanFilter,
    },
    shiftsWithBonus: {
      name: 'shiftsWithBonus',
      shortName: 'bonus',
      id: uuid.v4() as string,
      ...defaultBooleanFilter,
    },
    crewMembers: {
      name: 'crewMembers',
      id: uuid.v4() as string,
      ...defaultMultiSelectFilter,
      values: [],
    },
    stores: {
      name: 'stores',
      id: uuid.v4() as string,
      ...defaultMultiSelectFilter,
      values: [],
    },
    positions: {
      name: 'positions',
      id: uuid.v4() as string,
      ...defaultMultiSelectFilter,
      values: [],
    },
    incentives: {
      name: 'incentives',
      id: uuid.v4() as string,
      ...defaultSingleSelectFilter,
      values: enumToSettingValues(defaultIncentivesEnum),
    },
    startTime: {
      name: 'startTime',
      id: uuid.v4() as string,
      ...defaultSingleSelectFilter,
      values: enumToSettingValues(defaultStartTimeListEnum),
    },
  }
}

export function areDefaultFilters(filters: Filters) {
  const defaultFilters = getDefaultFilters()
  for (const [name, filter] of Object.entries(filters)) {
    switch (filter.type) {
      case 'boolean':
        if ((defaultFilters[filter.name] as BooleanFilter).on !== filter.on) {
          return false
        }
        break
      case 'multiSelect':
        if (
          !isEqual(
            (defaultFilters[filter.name] as MultiSelectFilter).selectedIds,
            filter.selectedIds
          )
        ) {
          return false
        }
        break
      case 'singleSelect':
        if (
          !isEqual(
            (defaultFilters[filter.name] as SingleSelectFilter).selectedId,
            filter.selectedId
          )
        ) {
          return false
        }
        break
    }
  }
  return true
}
